import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  ArrowLeft, Download, FileSpreadsheet, FileText, Search, Users,
  DollarSign, Clock, RefreshCw, Filter, Calendar, X, File, FileType, FileJson, BookOpen, Building,
  BarChart3, TrendingUp, AlertTriangle, Shield, Printer, Check, ChevronsUpDown
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { fmtBs, COLORS, CHART_COLORS_SKY } from "./reportesUtils";

applyPlugin(jsPDF);

const RecaudacionActEco = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [recActEcoData, setRecActEcoData] = useState(null);
  const [recActEcoFechaDesde, setRecActEcoFechaDesde] = useState("");
  const [recActEcoFechaHasta, setRecActEcoFechaHasta] = useState("");
  const [recActEcoFilters, setRecActEcoFilters] = useState({ codigo: "", nombre: "", alicuota: "", comprobantes: "", ingreso: "", impuesto: "", total: "", porcentaje: "" });

  useEffect(() => { fetchRecActEcoData(); }, []);

  const fetchRecActEcoData = async (aplicarFiltros = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (recActEcoFechaDesde) params.append("fecha_desde", recActEcoFechaDesde);
      if (recActEcoFechaHasta) params.append("fecha_hasta", recActEcoFechaHasta);
      const url = `${API}/reportes/recaudacion-por-actividad-economica?${params.toString()}`;
      const response = await axios.get(url, { headers });
      setRecActEcoData(response.data);
      if (aplicarFiltros) toast.success("Filtros aplicados correctamente");
    } catch (error) {
      toast.error("Error al cargar datos de recaudación por actividad económica");
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltrosRecActEco = () => {
    setRecActEcoFechaDesde("");
    setRecActEcoFechaHasta("");
    setRecActEcoFilters({ codigo: "", nombre: "", alicuota: "", comprobantes: "", ingreso: "", impuesto: "", total: "", porcentaje: "" });
    setTimeout(() => fetchRecActEcoData(false), 0);
    toast.info("Filtros eliminados");
  };

  const hayFiltrosRecActEcoActivos = recActEcoFechaDesde || recActEcoFechaHasta;
  const updateRecActEcoFilter = (key, value) => setRecActEcoFilters(prev => ({ ...prev, [key]: value }));
  const hayFiltrosRecActEcoColActivos = Object.values(recActEcoFilters).some(v => v !== "");

  const filteredRecActEcoFilas = recActEcoData?.filas?.filter(f => {
    const pf = recActEcoFilters;
    if (pf.codigo && !(f.codigo || "").toLowerCase().includes(pf.codigo.toLowerCase())) return false;
    if (pf.nombre && !(f.nombre_actividad || "").toLowerCase().includes(pf.nombre.toLowerCase())) return false;
    if (pf.alicuota && !String(f.alicuota || 0).includes(pf.alicuota)) return false;
    if (pf.comprobantes && !String(f.nro_comprobantes || 0).includes(pf.comprobantes)) return false;
    if (pf.ingreso && !String(f.ingreso_bruto || 0).includes(pf.ingreso)) return false;
    if (pf.impuesto && !String(f.impuesto_calculado || 0).includes(pf.impuesto)) return false;
    if (pf.total && !String(f.total_recaudado || 0).includes(pf.total)) return false;
    if (pf.porcentaje && !String(f.porcentaje || 0).includes(pf.porcentaje)) return false;
    return true;
  }) || [];

  const recActEcoTotalesFiltrados = filteredRecActEcoFilas.reduce((acc, f) => ({
    nro_comprobantes: acc.nro_comprobantes + (f.nro_comprobantes || 0),
    ingreso_bruto: acc.ingreso_bruto + (f.ingreso_bruto || 0),
    impuesto_calculado: acc.impuesto_calculado + (f.impuesto_calculado || 0),
    total_recaudado: acc.total_recaudado + (f.total_recaudado || 0),
  }), { nro_comprobantes: 0, ingreso_bruto: 0, impuesto_calculado: 0, total_recaudado: 0 });


  const exportRecActEcoExcel = () => {
    if (!recActEcoData) return;
    const wsData = [
      ["RECAUDACIÓN POR CÓDIGO ACTIVIDAD ECONÓMICA"], [],
      ["CÓDIGO", "TIPO ACT. ECONÓMICA", "ALÍCUOTA %", "NRO. COMP.", "INGRESO BRUTO", "IMPUESTO CALC.", "TOTAL RECAUDADO", "%"]
    ];
    filteredRecActEcoFilas.forEach(f => {
      wsData.push([f.codigo, f.nombre_actividad, f.alicuota, f.nro_comprobantes, f.ingreso_bruto, f.impuesto_calculado, f.total_recaudado, f.porcentaje]);
    });
    wsData.push([]);
    wsData.push(["", "TOTALES", "", recActEcoTotalesFiltrados.nro_comprobantes, recActEcoTotalesFiltrados.ingreso_bruto, recActEcoTotalesFiltrados.impuesto_calculado, recActEcoTotalesFiltrados.total_recaudado, "100%"]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "RecActEconomica");
    XLSX.writeFile(wb, `Recaudacion_Act_Economica_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exportado a Excel");
  };

  const exportRecActEcoPDF = () => {
    if (!recActEcoData) return;
    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(14);
    doc.setTextColor(2, 132, 199);
    doc.text("RECAUDACIÓN POR CÓDIGO ACTIVIDAD ECONÓMICA", 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Total recaudado: Bs. ${recActEcoTotalesFiltrados.total_recaudado.toLocaleString("es-VE", {minimumFractionDigits: 2})}`, 14, 25);
    const headCols = ["Código", "Tipo Act. Económica", "Alícuota", "Comp.", "Ingreso Bruto", "Imp. Calc.", "Total Recaudado", "%"];
    const body = filteredRecActEcoFilas.map(f => [f.codigo, f.nombre_actividad, f.alicuota + "%", f.nro_comprobantes, fmtBs(f.ingreso_bruto), fmtBs(f.impuesto_calculado), fmtBs(f.total_recaudado), f.porcentaje + "%"]);
    body.push(["", "TOTALES", "", recActEcoTotalesFiltrados.nro_comprobantes, fmtBs(recActEcoTotalesFiltrados.ingreso_bruto), fmtBs(recActEcoTotalesFiltrados.impuesto_calculado), fmtBs(recActEcoTotalesFiltrados.total_recaudado), "100%"]);
    doc.autoTable({
      startY: 32, head: [headCols], body,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [2, 132, 199], textColor: 255 },
      alternateRowStyles: { fillColor: [240, 249, 255] },
      didParseCell: (data) => { if (data.row.index === body.length - 1) { data.cell.styles.fontStyle = "bold"; data.cell.styles.fillColor = [186, 230, 253]; } }
    });
    doc.save(`Recaudacion_Act_Economica_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exportado a PDF");
  };

  const exportRecActEcoCSV = () => {
    if (!recActEcoData) return;
    const cols = ["CÓDIGO", "TIPO ACT. ECONÓMICA", "ALÍCUOTA", "NRO. COMP.", "INGRESO BRUTO", "IMP. CALCULADO", "TOTAL RECAUDADO", "%"];
    const rows = filteredRecActEcoFilas.map(f => [`"${f.codigo}"`, `"${f.nombre_actividad}"`, f.alicuota, f.nro_comprobantes, f.ingreso_bruto, f.impuesto_calculado, f.total_recaudado, f.porcentaje].join(","));
    const blob = new Blob(["\ufeff" + cols.join(",") + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `Recaudacion_Act_Economica_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("Exportado a CSV");
  };

  const chartData = filteredRecActEcoFilas.map(f => ({
    nombre: (f.codigo + " - " + f.nombre_actividad).length > 35 ? (f.codigo + " - " + f.nombre_actividad).substring(0, 35) + "..." : f.codigo + " - " + f.nombre_actividad,
    nombre_completo: f.codigo + " - " + f.nombre_actividad,
    total: f.total_recaudado,
    porcentaje: f.porcentaje
  }));
  return (
    <div className="space-y-6" data-testid="reporte-recaudacion-actividad-economica">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reportes")} data-testid="recae-btn-volver">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-sky-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recaudación por Código Actividad Económica
            </h1>
            <p className="text-slate-500">Ingresos recaudados agrupados por código y tipo de actividad económica</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => fetchRecActEcoData(false)} disabled={loading} data-testid="recae-btn-actualizar">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={!recActEcoData} data-testid="recae-btn-exportar">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportRecActEcoExcel} data-testid="recae-export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportRecActEcoPDF} data-testid="recae-export-pdf">
                <FileText className="w-4 h-4 mr-2" /> PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportRecActEcoCSV} data-testid="recae-export-csv">
                <File className="w-4 h-4 mr-2" /> CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filtros superiores */}
      <Card className="border-sky-200 bg-sky-50/30">
        <CardContent className="p-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-[170px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Fecha Desde</Label>
              <Input type="date" value={recActEcoFechaDesde} onChange={(e) => setRecActEcoFechaDesde(e.target.value)} className="bg-white" data-testid="recae-fecha-desde" />
            </div>
            <div className="w-[170px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Fecha Hasta</Label>
              <Input type="date" value={recActEcoFechaHasta} onChange={(e) => setRecActEcoFechaHasta(e.target.value)} className="bg-white" data-testid="recae-fecha-hasta" />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => fetchRecActEcoData(true)} disabled={loading} className="bg-sky-600 hover:bg-sky-700" data-testid="recae-btn-aplicar">
                <Search className="w-4 h-4 mr-2" /> Aplicar
              </Button>
              {hayFiltrosRecActEcoActivos && (
                <Button variant="outline" onClick={limpiarFiltrosRecActEco} disabled={loading} data-testid="recae-btn-limpiar">
                  <X className="w-4 h-4 mr-2" /> Limpiar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-sky-400" />
        </div>
      ) : recActEcoData ? (
        <>
          {/* Tarjetas resumen */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Total Recaudado</p>
                <p className="text-lg font-bold text-sky-700" data-testid="recae-card-total">{fmtBs(recActEcoData.resumen?.total_recaudado)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Total Comprobantes</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="recae-card-comprobantes">{recActEcoData.resumen?.total_comprobantes || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Actividades con Recaudación</p>
                <p className="text-2xl font-bold text-sky-600" data-testid="recae-card-actividades">{recActEcoData.resumen?.total_actividades || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Promedio por Actividad</p>
                <p className="text-lg font-bold text-amber-600" data-testid="recae-card-promedio">{fmtBs(recActEcoData.resumen?.promedio_por_actividad)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Gráfico de barras horizontal */}
          {chartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Distribución por Actividad Económica</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ width: "100%", height: Math.max(chartData.length * 50 + 40, 200) }} data-testid="recae-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tickFormatter={(val) => `Bs. ${(val / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="nombre" width={260} tick={{ fontSize: 11 }} />
                      <Tooltip
                        formatter={(val) => [`Bs. ${val.toLocaleString("es-VE", {minimumFractionDigits: 2})}`, "Recaudado"]}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.nombre_completo || label}
                      />
                      <Bar dataKey="total" name="Total Recaudado" radius={[0, 6, 6, 0]}>
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS_SKY[index % CHART_COLORS_SKY.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabla */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Detalle por Actividad Económica</CardTitle>
                  <CardDescription>{filteredRecActEcoFilas.length} de {recActEcoData.filas?.length || 0} actividades</CardDescription>
                </div>
                {hayFiltrosRecActEcoColActivos && (
                  <Button variant="outline" size="sm" onClick={() => setRecActEcoFilters({ codigo: "", nombre: "", alicuota: "", comprobantes: "", ingreso: "", impuesto: "", total: "", porcentaje: "" })} data-testid="recae-limpiar-filtros-col">
                    <X className="w-4 h-4 mr-1" /> Limpiar filtros
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="recae-tabla">
                  <thead>
                    <tr className="border-b-2 border-sky-200 bg-sky-50">
                      <th className="text-left py-3 px-3 text-xs font-semibold text-sky-800 uppercase">Código</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-sky-800 uppercase">Tipo Act. Económica</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-sky-800 uppercase">Alícuota</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-sky-800 uppercase">Comp.</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-sky-800 uppercase">Ingreso Bruto</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-sky-800 uppercase">Imp. Calculado</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-sky-800 uppercase">Total Recaudado</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-sky-800 uppercase">%</th>
                    </tr>
                    <tr className="bg-sky-50/60 border-b border-sky-100">
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={recActEcoFilters.codigo} onChange={(e) => updateRecActEcoFilter("codigo", e.target.value)} data-testid="recae-filter-codigo" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={recActEcoFilters.nombre} onChange={(e) => updateRecActEcoFilter("nombre", e.target.value)} data-testid="recae-filter-nombre" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recActEcoFilters.alicuota} onChange={(e) => updateRecActEcoFilter("alicuota", e.target.value)} data-testid="recae-filter-alicuota" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recActEcoFilters.comprobantes} onChange={(e) => updateRecActEcoFilter("comprobantes", e.target.value)} data-testid="recae-filter-comprobantes" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recActEcoFilters.ingreso} onChange={(e) => updateRecActEcoFilter("ingreso", e.target.value)} data-testid="recae-filter-ingreso" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recActEcoFilters.impuesto} onChange={(e) => updateRecActEcoFilter("impuesto", e.target.value)} data-testid="recae-filter-impuesto" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recActEcoFilters.total} onChange={(e) => updateRecActEcoFilter("total", e.target.value)} data-testid="recae-filter-total" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recActEcoFilters.porcentaje} onChange={(e) => updateRecActEcoFilter("porcentaje", e.target.value)} data-testid="recae-filter-porcentaje" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecActEcoFilas.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-12 text-slate-400">No se encontraron datos de recaudación por actividad económica</td></tr>
                    ) : (
                      filteredRecActEcoFilas.map((f, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-sky-50/30 transition-colors">
                          <td className="py-2.5 px-3 text-sm">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-800 font-mono">{f.codigo}</span>
                          </td>
                          <td className="py-2.5 px-3 text-sm text-slate-800 font-medium">{f.nombre_actividad}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{f.alicuota}%</td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{f.nro_comprobantes}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{fmtBs(f.ingreso_bruto)}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{fmtBs(f.impuesto_calculado)}</td>
                          <td className="py-2.5 px-3 text-sm text-right font-bold text-sky-700">{fmtBs(f.total_recaudado)}</td>
                          <td className="py-2.5 px-3 text-sm text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 bg-slate-100 rounded-full h-2">
                                <div className="bg-sky-500 h-2 rounded-full" style={{ width: `${Math.min(f.porcentaje, 100)}%` }}></div>
                              </div>
                              <span className="text-xs font-semibold text-slate-600 w-10 text-right">{f.porcentaje}%</span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {filteredRecActEcoFilas.length > 0 && (
                    <tfoot>
                      <tr className="bg-sky-100 border-t-2 border-sky-300">
                        <td colSpan={3} className="py-3 px-3 text-sm font-bold text-sky-900 uppercase">Totales ({filteredRecActEcoFilas.length} actividades)</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-sky-900">{recActEcoTotalesFiltrados.nro_comprobantes}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-sky-900">{fmtBs(recActEcoTotalesFiltrados.ingreso_bruto)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-sky-900">{fmtBs(recActEcoTotalesFiltrados.impuesto_calculado)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-sky-900">{fmtBs(recActEcoTotalesFiltrados.total_recaudado)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-sky-900">100%</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se pudieron cargar los datos del reporte</p>
            <Button variant="outline" onClick={() => fetchRecActEcoData()} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

};

export default RecaudacionActEco;
